import { Contact } from '@/types/crm';
import { getLeadLastInteractionDate } from '@/lib/leadUtils';

export type LeadPersonaKey =
  | 'decisor_rapido'
  | 'explorador'
  | 'cetico'
  | 'dorminhoco'
  | 'busy_executive'
  | 'detail_planner'
  | 'discount_hunter'
  | 'quick_decider'
  | 'researcher'
  | 'unknown';

export interface LeadScoreFactor {
  factor: string;
  points: number;
  max_points: number;
}

export interface LeadScoreResult {
  score: number;
  conversionProbability: number;
  factors: LeadScoreFactor[];
  stagePoints: number;
  engagementPoints: number;
  recencyPoints: number;
}

const normalizeText = (value?: string) =>
  (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export function getLeadTemperaturePoints(lead: Contact): number {
  const temp = normalizeText(lead.temperatura_lead || lead.temperatura || lead.temp);
  if (temp === 'quente') return 30;
  if (temp === 'morno') return 15;
  return 0;
}

export function getLeadStagePoints(lead: Contact): number {
  const stage = normalizeText(lead.etapa_atual || lead.etapa || lead.stage || lead.status);
  if (!stage) return 5;
  if (stage.includes('confirm') || stage.includes('pag') || stage.includes('fech')) return 25;
  if (stage.includes('negoc') || stage.includes('propost') || stage.includes('orc') || stage.includes('custom')) return 18;
  if (stage.includes('interess') || stage.includes('qualific') || stage.includes('reun')) return 12;
  return 8;
}

function estimateInteractions(lead: Contact): number {
  const numericInteractions = Number((lead.interactions_count || lead.interacoes || lead.total_interacoes || 0) as string);
  if (Number.isFinite(numericInteractions) && numericInteractions > 0) return numericInteractions;

  const notes = String(lead.observacoes || '').trim();
  if (!notes) return 0;
  const segments = notes.split(/\n|\|/).map((s) => s.trim()).filter(Boolean);
  return Math.max(1, segments.length);
}

export function getLeadEngagementPoints(lead: Contact): number {
  const interactions = estimateInteractions(lead);
  if (interactions >= 5) return 20;
  if (interactions >= 3) return 14;
  if (interactions >= 1) return 8;
  return 0;
}

export function getLeadRecencyPoints(lead: Contact): number {
  const lastInteraction = getLeadLastInteractionDate(lead);
  if (!lastInteraction) return 0;

  const diffDays = Math.floor((Date.now() - lastInteraction.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 1) return 15;
  if (diffDays <= 3) return 12;
  if (diffDays <= 7) return 8;
  if (diffDays <= 14) return 4;
  return 0;
}

export function getLeadCompletenessPoints(lead: Contact): number {
  const importantFields = [
    lead.nome_cliente,
    lead.telefone,
    lead.categoria_produto,
    lead.etapa_atual || lead.etapa || lead.stage,
    lead.temperatura_lead || lead.temperatura || lead.temp,
    lead.observacoes,
    lead.data_primeira_mensagem,
    lead.data_ultima_atualizacao,
  ];

  const filled = importantFields.filter((field) => Boolean(String(field || '').trim())).length;
  return Math.round((filled / importantFields.length) * 10);
}

export function calculateLeadScore(lead: Contact): LeadScoreResult {
  const temperaturePoints = getLeadTemperaturePoints(lead);
  const stagePoints = getLeadStagePoints(lead);
  const engagementPoints = getLeadEngagementPoints(lead);
  const recencyPoints = getLeadRecencyPoints(lead);
  const completenessPoints = getLeadCompletenessPoints(lead);

  const score = clamp(
    temperaturePoints + stagePoints + engagementPoints + recencyPoints + completenessPoints,
    0,
    100,
  );

  return {
    score,
    conversionProbability: clamp(score / 100, 0, 1),
    stagePoints,
    engagementPoints,
    recencyPoints,
    factors: [
      { factor: 'Temperatura', points: temperaturePoints, max_points: 30 },
      { factor: 'Etapa do funil', points: stagePoints, max_points: 25 },
      { factor: 'Engajamento', points: engagementPoints, max_points: 20 },
      { factor: 'Tempo no CRM', points: recencyPoints, max_points: 15 },
      { factor: 'Completude', points: completenessPoints, max_points: 10 },
    ],
  };
}

export function calculatePersona(lead: Contact, score?: number): LeadPersonaKey {
  const finalScore = score ?? calculateLeadScore(lead).score;
  const temperature = normalizeText(lead.temperatura_lead || lead.temperatura || lead.temp);
  const stagePoints = getLeadStagePoints(lead);
  const engagementPoints = getLeadEngagementPoints(lead);
  const recencyPoints = getLeadRecencyPoints(lead);
  const notes = normalizeText(lead.observacoes || '');

  const hasObjection =
    notes.includes('caro') || notes.includes('duvida') || notes.includes('dúvida') || notes.includes('talvez') || notes.includes('depois');

  if (finalScore >= 80 && temperature === 'quente' && stagePoints >= 18) {
    return 'decisor_rapido';
  }

  if (finalScore >= 50 && finalScore < 80 && engagementPoints >= 8 && stagePoints >= 12) {
    return 'explorador';
  }

  if (temperature === 'frio' && (hasObjection || stagePoints <= 8)) {
    return 'cetico';
  }

  if (finalScore < 45 && recencyPoints <= 4) {
    return 'dorminhoco';
  }

  if (temperature === 'quente') return 'quick_decider';
  if (notes.length > 80) return 'researcher';
  return 'detail_planner';
}

export function buildLeadAnalysisSignature(lead: Contact): string {
  const entries = Object.entries(lead)
    .filter(([, value]) => typeof value === 'string' && Boolean(value))
    .sort(([a], [b]) => a.localeCompare(b));

  return entries.map(([key, value]) => `${key}:${value}`).join('|');
}

export function getRecommendedAction(lead: Contact, persona: LeadPersonaKey): string {
  if (persona === 'decisor_rapido' || persona === 'quick_decider') {
    return 'Responder com proposta objetiva e CTA de fechamento imediato.';
  }
  if (persona === 'explorador' || persona === 'researcher') {
    return 'Enviar comparação curta com próximos passos e prazo de retorno.';
  }
  if (persona === 'cetico' || persona === 'discount_hunter') {
    return 'Quebrar objeções com prova social e proposta de valor clara.';
  }
  if (persona === 'dorminhoco') {
    return 'Reativar com mensagem curta de retomada e oferta de horário.';
  }
  return 'Avançar para o próximo passo do funil com confirmação explícita.';
}

export function getPersonaPresentation(persona: LeadPersonaKey) {
  const map: Record<LeadPersonaKey, { label: string; emoji: string }> = {
    decisor_rapido: { label: 'Decisor Rápido', emoji: '⚡' },
    explorador: { label: 'Explorador', emoji: '🧭' },
    cetico: { label: 'Cético', emoji: '🧊' },
    dorminhoco: { label: 'Dorminhoco', emoji: '😴' },
    busy_executive: { label: 'Executivo Apressado', emoji: '💼' },
    detail_planner: { label: 'Planejador Detalhista', emoji: '📋' },
    discount_hunter: { label: 'Caçador de Desconto', emoji: '💸' },
    quick_decider: { label: 'Decisor Rápido', emoji: '⚡' },
    researcher: { label: 'Pesquisador', emoji: '🔎' },
    unknown: { label: 'Sem classificação', emoji: '❔' },
  };

  return map[persona] || map.unknown;
}
